import { FunctionComponent, useEffect, useState } from 'react'
import { ChildDaycareWithParents, ChildParent, ChildSection } from '@/types/Child'
import styles from './daycareChildProfile.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import UserInput from '@/components/_custom/UserInput/userInput'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import PriorityGroupSelector from './components/PriorityGroupSelector/priorityGroupSelector'
import { usePriorityGroup } from './components/PriorityGroupSelector/usePriorityGroup'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import Title from '@/components/_custom/Title/title'
import { KeyboardArrowDown, KeyboardArrowUp, Visibility, VisibilityOff } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { UPDATE_CHILD_LINK, UPDATE_CHILD_VISIBILITY } from '@/modules/API/parent/childHandler'
import AdultTable from './adultTable/adultTable'
import ChildPlanningHandler from './childPlanningHandler/childPlanningHandler'
import TaskAndNotesChild from '../taskAndNotes/taskAndNotesChild.tsx'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import MealChild from '@/container/families/childProfile/daycareChildProfile/mealChild/mealChild.tsx'

interface DaycareChildProfileProps {
    daycareChild: ChildDaycareWithParents
    childId: number
    childName: string
    canUpdate: boolean
    selectOptionsSection: SelectOptionsDeprecated
    selectOptionsDaycare: SelectOptionsDeprecated
    setSelectedDaycareChild: (id: string) => void
    handleRefetchChild: () => void
    showRegular: boolean
}

const DaycareChildProfile: FunctionComponent<DaycareChildProfileProps> = ({
    daycareChild,
    childName,
    childId,
    canUpdate,
    selectOptionsSection,
    selectOptionsDaycare,
    setSelectedDaycareChild,
    handleRefetchChild,
    showRegular
}: DaycareChildProfileProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [showDaycareInformation, setShowDaycareInformation] = useState<boolean>(!showRegular)

    const [softwareId, setSoftwareId] = useState<string>(daycareChild.softwareId?.toString() || '')

    const [visible, setVisible] = useState<boolean>(daycareChild.visible)
    const [complement, setComplement] = useState<string>(daycareChild.complement || '')
    const { selectedGroup, setSelectedGroup } = usePriorityGroup(daycareChild)

    const [selectedSection, setSelectedSection] = useState<string[]>(
        daycareChild.sections.map((section: ChildSection) => section.sectionId.toString())
    )
    const [primarySection, setPrimarySection] = useState<string>(
        daycareChild.sections.find((section: ChildSection) => section.isPrimary)?.sectionId.toString() || ''
    )

    const [selectOptionsPrimarySection, setSelectOptionsPrimarySection] = useState<SelectOptionsDeprecated>([
        {
            options: daycareChild.sections
                .filter((section: ChildSection) => selectedSection.includes(section.sectionId.toString()))
                .map((section: ChildSection): SelectOptionDeprecated => {
                    return {
                        name: section.sectionName,
                        value: section.sectionId.toString()
                    }
                })
        }
    ])

    function handleSelectedSection(value: string[]): void {
        setSelectedSection(value)
        setSelectOptionsPrimarySection([
            {
                options: selectOptionsSection[0].options.filter((section: SelectOptionDeprecated) =>
                    value.includes(section.value)
                )
            }
        ])
    }

    const [updateChildVisibility] = useMutation(UPDATE_CHILD_VISIBILITY, {
        variables: {
            child_id: childId,
            daycare_id: daycareChild.daycareId,
            visibility: !visible
        },
        onCompleted: (): void => {
            showNotificationSuccess(t('childUpdated'))

            handleRefetchChild()
            setVisible(!visible)
        },
        onError: (): void => {
            showNotificationError(t('errorUpdatingChild'))

            handleRefetchChild()
        }
    })

    const [updateChildLink] = useMutation(UPDATE_CHILD_LINK, {
        variables: {
            childId: childId,
            daycareId: daycareChild.daycareId,
            complement: complement,
            sectionIds: selectedSection.map((section: string) => parseInt(section)),
            primarySectionId:
                selectedSection.length > 1 && primarySection ? parseInt(primarySection) : parseInt(selectedSection[0]),
            groupId: selectedGroup && selectedGroup !== '' ? parseInt(selectedGroup) : undefined
        },
        onCompleted: (): void => {
            showNotificationSuccess(t('childUpdated'))

            handleRefetchChild()
        },
        onError: (): void => {
            showNotificationError(t('errorUpdatingChild'))

            handleRefetchChild()
        }
    })

    const disabled: boolean =
        !canUpdate ||
        (visible === daycareChild.visible &&
            complement === (daycareChild.complement || '') &&
            selectedGroup === (daycareChild.group ? daycareChild.group.groupId.toString() : '') &&
            JSON.stringify(selectedSection) ===
                JSON.stringify(daycareChild.sections.map((section: ChildSection) => section.sectionId.toString())) &&
            primarySection ===
                (daycareChild.sections.find((section: ChildSection) => section.isPrimary)?.sectionId.toString() ||
                    '')) ||
        (selectedSection.length > 1 && !primarySection)

    useEffect(() => {
        setSoftwareId(daycareChild.softwareId?.toString() || '')

        setVisible(daycareChild.visible)
        setComplement(daycareChild.complement || '')

        setSelectedSection(daycareChild.sections.map((section: ChildSection) => section.sectionId.toString()))

        setPrimarySection(
            daycareChild.sections.find((section: ChildSection) => section.isPrimary)?.sectionId.toString() || ''
        )
    }, [daycareChild])

    const isRpeAndHaveNanny: boolean =
        daycareChild.parents.some((parent: ChildParent) => parent.isNanny) && daycareChild.isRpeOrLaep

    return (
        <>
            <div className={styles.Form}>
                <div className={styles.TitleAndSelector}>
                    <div className={styles.Title}>
                        <Title text={t('daycareInformation')} />
                        {showDaycareInformation ? (
                            <KeyboardArrowUp
                                onClick={() => setShowDaycareInformation(false)}
                                className={styles.Arrow}
                            />
                        ) : (
                            <KeyboardArrowDown
                                onClick={() => setShowDaycareInformation(true)}
                                className={styles.Arrow}
                            />
                        )}
                    </div>
                    {selectOptionsDaycare[0].options.length > 1 && (
                        <div className={styles.Input}>
                            <SelectUnique
                                selectOptions={selectOptionsDaycare}
                                selectValue={daycareChild.daycareId.toString()}
                                setSelectValue={setSelectedDaycareChild}
                                label={t('structure')}
                                className={styles.UserInput}
                            />
                        </div>
                    )}
                </div>
                {showDaycareInformation && (
                    <>
                        {visible ? (
                            <>
                                <div className={styles.InputContainer}>
                                    {softwareId !== '' && (
                                        <div className={styles.Input}>
                                            <UserInput
                                                label={t('softwareId')}
                                                disabled={true}
                                                className={styles.UserInput}
                                                readOnly={true}
                                                value={softwareId}
                                            />
                                        </div>
                                    )}
                                    <div className={styles.Input}>
                                        <UserInput
                                            label={t('complement')}
                                            disabled={!canUpdate}
                                            value={complement}
                                            fullWidth={true}
                                            setValue={setComplement}
                                        />
                                        <PriorityGroupSelector
                                            daycareChild={daycareChild}
                                            selectValue={selectedGroup}
                                            setSelectValue={setSelectedGroup}
                                        />
                                    </div>
                                    {selectOptionsSection.length > 0 && (
                                        <>
                                            <div className={styles.Input}>
                                                <SelectMultiple
                                                    selectOptions={selectOptionsSection}
                                                    selectValues={selectedSection}
                                                    setSelectValues={handleSelectedSection}
                                                    readOnly={!canUpdate || isRpeAndHaveNanny}
                                                    label={daycareChild.isRpeOrLaep ? t('sector') : t('section')}
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                            {selectedSection.length > 1 && (
                                                <div className={styles.Input}>
                                                    <SelectUnique
                                                        selectOptions={selectOptionsPrimarySection}
                                                        selectValue={primarySection}
                                                        required
                                                        setSelectValue={setPrimarySection}
                                                        label={
                                                            daycareChild.isRpeOrLaep
                                                                ? t('primarySector')
                                                                : t('primarySection')
                                                        }
                                                        formClassName={styles.UserInput}
                                                        readOnly={!canUpdate || isRpeAndHaveNanny}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                {canUpdate && (
                                    <div className={styles.ButtonContainer}>
                                        <ButtonTextIcon
                                            type={'custom'}
                                            className={styles.Button}
                                            customText={t('hideChild')}
                                            onClick={updateChildVisibility}
                                            disabled={!canUpdate}
                                            customIcon={<VisibilityOff />}
                                        />
                                        <ButtonTextIcon
                                            type={'update'}
                                            className={styles.Button}
                                            disabled={disabled}
                                            onClick={updateChildLink}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.InputContainer}>
                                <div className={styles.ButtonContainer}>
                                    <ButtonTextIcon
                                        type={'custom'}
                                        customIcon={<Visibility />}
                                        customText={t('setVisibleChild')}
                                        className={styles.Button}
                                        disabled={!canUpdate}
                                        onClick={updateChildVisibility}
                                    />
                                </div>
                            </div>
                        )}
                        <div className={styles.Form}>
                            <AdultTable
                                parents={daycareChild.parents}
                                daycareId={daycareChild.daycareId}
                                childId={childId}
                                childName={`${childName} ${complement ? ' - ' + complement : ''}`}
                                handleRefetchChild={handleRefetchChild}
                                isRpeOrLaep={daycareChild.isRpeOrLaep}
                            />
                        </div>
                    </>
                )}
            </div>
            {visible && user?.currentDaycareRights.canReadContracts && (
                <div className={styles.Form}>
                    <ChildPlanningHandler daycareId={daycareChild.daycareId} childId={childId} />
                </div>
            )}
            {user?.currentDaycareRights.canReadTaskNote && user.currentDaycare?.taskNoteAccess && (
                <TaskAndNotesChild
                    daycare={daycareChild}
                    childId={childId}
                    childName={childName}
                    parents={daycareChild.parents}
                    handleRefetchChild={handleRefetchChild}
                    showRegular={showRegular}
                    canUpdate={user.currentDaycareRights.canWriteTaskNote}
                />
            )}
            {user?.currentDaycareRights.canReadMeal && user.currentDaycare?.mealAccess && (
                <MealChild childId={childId} showRegular={showRegular} />
            )}
        </>
    )
}

export default DaycareChildProfile
